/* eslint-disable no-unused-vars, no-loop-func */
// ═══════════════════════════════════════════════════════════════
// FK-5-1-12 STEADY-STATE FLOW ESTIMATION ENGINE v1.0
// Hydraulic network solver for Chemori 5112 suppression systems
// Based on Chemori FK5112 Engineered Manual Rev 1.0 (UL EX27222)
// ═══════════════════════════════════════════════════════════════

// ─── SECTION 1: CONSTANTS & REFERENCE DATA ───────────────────

// FK-5-1-12 (Novec 1230) physical properties
var AGENT = {
  liquidDensity: 1600,     // kg/m³ at 20°C
  viscosity: 0.00036,      // Pa·s (dynamic viscosity at 25°C)
  molecularWeight: 316.04, // g/mol
  boilingPoint: 49.2,      // °C
  nozzleCd: 0.97,          // Discharge coefficient (industry standard)
  pipeRoughness: 0.046e-3, // m (commercial steel, 0.046mm)
};

// Superpressurization: N₂ at 35 bar (500 psi) at 20°C
// Linear fit from Table 4-1: P(bar) = 30.9 + 0.172 × T(°C)
function cylinderPressure(tempC) {
  return 30.9 + 0.172 * tempC; // bar (gauge) — initial peak pressure
}

// Mean effective pressure factor for N₂ superpressurized FK-5-1-12 systems.
// During discharge, N₂ expands as liquid agent exits the cylinder, causing
// continuous pressure decay from initial peak (~34.5 bar at 21°C) to ~5 bar.
// The time-averaged effective driving pressure is approximately 40% of the
// initial superpressure. Validated against Chemori FC4 transient simulation:
// initial 34.5 bar → effective 14.0 bar (ratio 0.406).
var MEAN_EFFECTIVE_PRESSURE_FACTOR = 0.406;

// Schedule 40 steel pipe inner diameters (m)
var PIPE_ID = {
  "15":  0.01580, // 1/2"
  "20":  0.02093, // 3/4"
  "25":  0.02664, // 1"
  "32":  0.03505, // 1-1/4"
  "40":  0.04089, // 1-1/2"
  "50":  0.05250, // 2"
  "65":  0.06271, // 2-1/2"
  "80":  0.07793, // 3"
  "100": 0.10226, // 4"
  "125": 0.12819, // 5"
  "150": 0.15405, // 6"
};

// Pipe cross-sectional area (m²)
function pipeArea(diamMM) {
  var d = PIPE_ID[String(diamMM)] || PIPE_ID["25"];
  return Math.PI * d * d / 4;
}

// Open-end pipe area in in² (for nozzle orifice ratio checks)
var OPEN_PIPE_AREA_IN2 = {
  "15": 0.3039, "20": 0.5333, "25": 0.8643, "32": 1.4957,
  "40": 2.0358, "50": 3.3557, "65": 4.7855, "80": 7.3931,
  "100": 12.7300, "125": 20.006, "150": 28.89,
};

// Flow rate limits (lb/s) from Table 3-4
var FLOW_LIMITS = {
  "15":  [0.7, 3.4],    // 1/2"
  "20":  [2.0, 5.8],    // 3/4"
  "25":  [3.4, 8.4],    // 1"
  "32":  [5.8, 13.0],   // 1-1/4"
  "40":  [8.4, 19.5],   // 1-1/2"
  "50":  [13.0, 33.0],  // 2"
  "65":  [19.5, 58.0],  // 2-1/2"
  "80":  [33.0, 95.0],  // 3"
  "100": [58.0, 127.0], // 4"
  "125": [95.0, 222.0], // 5"
  "150": [127.0, 317.8],// 6"
};

// Equivalent lengths (ft) of 300 lb Malleable Threaded Fittings (Table 3-6)
var EQ_LEN_THREADED = {
  // [90°elbow, 45°elbow, thruTee, sideTee, unionCoupling, unionElbow]
  "15":  [1.7, 0.8, 1.0, 3.4, 0.4, 2.1],
  "20":  [2.2, 1.0, 1.4, 4.5, 0.5, 2.7],
  "25":  [2.8, 1.3, 1.8, 5.7, 0.6, 3.4],
  "32":  [3.7, 1.7, 2.3, 7.5, 0.9, 5.2],
  "40":  [4.3, 2.0, 2.7, 8.7, 0.9, 5.2],
  "50":  [5.5, 2.6, 3.5, 11.2, 1.2, 6.7],
  "65":  [6.5, 3.1, 4.1, 13.4, 1.4, 8.0],
  "80":  [8.2, 3.8, 5.1, 16.8, 1.8, 10.0],
  "100": [10.7, 5.0, 6.7, 21.8, 2.4, 13.1],
  "125": [13.4, 6.3, 8.4, 27.4, 3.0, 16.4],
  "150": [16.2, 7.6, 10.1, 32.8, 3.5, 19.7],
};

// Valve+Siphon and Hose equivalent lengths (ft) from Table 3-9
var EQ_LEN_VALVE_HOSE = {
  // valveSize: [valveAndSiphon, dischargeFlexHose]
  "25":  [16.5, 7.0],   // 1" valve
  "40":  [28.3, 7.6],   // 1.5" valve
  "65":  [30.8, 11.6],  // 2.5" valve
  "80":  [50.5, 10.0],  // 3" valve
  "100": [75.0, 10.0],  // 4" valve
};

// NFPA 2001 / Chemori Table 3-5 constraints
var CONSTRAINTS = {
  minDischargeTime: 6,        // seconds
  maxDischargeTime: 10,       // seconds
  maxAgentInPipe: 82.1,       // %
  minAgentBeforeFirstTee: 9.3,// %
  minNozzlePressure: 4.69,    // bar gauge (68 psig)
  maxArrivalImbalance: 1.02,  // seconds
  maxRunOutImbalance: 2.16,   // seconds
  maxElevationDiff: 9.14,     // m (30 ft)
  maxNozzleHeight: 4.88,      // m (16 ft)
  minConcentration: 4.5,      // % by volume
  // Tee constraints
  bullheadTeeMin: 0.30,       // 30% minimum split
  bullheadTeeMax: 0.70,       // 70% maximum split
  thruTeeSideMin: 0.10,       // 10% minimum side outlet
  thruTeeSideMax: 0.35,       // 35% maximum side outlet
  minElbowToTeeDist: 10,      // nominal pipe diameters
  minTeeSpacing: 10,          // nominal pipe diameters
};

// Nozzle size to pipe size mapping (nozzle inlet → nominal pipe mm)
var NOZZLE_PIPE_MAP = {
  "1/2 Inch": "15", "3/4 Inch": "20", "1 Inch": "25",
  "1.25 Inch": "32", "1.5 Inch": "40", "2 Inch": "50",
};
var NOZZLE_PIPE_MM = {
  "70704": "15", "70705": "20", "70706": "25",
  "70707": "32", "70708": "40", "70709": "50",
};

// Cylinder data lookup (keyed by model pattern)
var CYLINDERS = [
  { model: "70020", lbs: 20,   capL: 8.2,   minKg: 5,    maxKg: 9.5,   valveMM: 25,  hosePn: "CR 60255", tare: 15 },
  { model: "70035", lbs: 35,   capL: 14.5,  minKg: 8.2,  maxKg: 17.2,  valveMM: 25,  hosePn: "CR 60255", tare: 18.5 },
  { model: "70070", lbs: 70,   capL: 28.9,  minKg: 16.3, maxKg: 34.5,  valveMM: 25,  hosePn: "CR 60255", tare: 25 },
  { model: "70100", lbs: 100,  capL: 40.8,  minKg: 23.1, maxKg: 49,    valveMM: 25,  hosePn: "CR 60255", tare: 35 },
  { model: "70150", lbs: 150,  capL: 61.7,  minKg: 34.9, maxKg: 73.9,  valveMM: 40,  hosePn: "CR 60256", tare: 49 },
  { model: "70250", lbs: 250,  capL: 102.5, minKg: 57.6, maxKg: 122.9, valveMM: 40,  hosePn: "CR 60256", tare: 65 },
  { model: "70375", lbs: 375,  capL: 153.4, minKg: 86.2, maxKg: 184.1, valveMM: 65,  hosePn: "CR 60257", tare: 93 },
  { model: "70560", lbs: 560,  capL: 227,   minKg: 127.4,maxKg: 272.6, valveMM: 65,  hosePn: "CR 60257", tare: 134 },
  { model: "70650", lbs: 650,  capL: 268.9, minKg: 151,  maxKg: 322.9, valveMM: 80,  hosePn: "CR 91230-C", tare: 148 },
  { model: "70800", lbs: 800,  capL: 339.7, minKg: 190.5,maxKg: 408.2, valveMM: 80,  hosePn: "CR 91230-C", tare: 249 },
  { model: "71000", lbs: 1000, capL: 419.8, minKg: 235.8,maxKg: 504.3, valveMM: 80,  hosePn: "CR 91230-C", tare: 284 },
  { model: "71200", lbs: 1200, capL: 489.8, minKg: 274.8,maxKg: 588.2, valveMM: 100, hosePn: "CR 91230", tare: 347 },
];

function findCylinder(modelStr) {
  if (!modelStr) return null;
  var m = modelStr.toUpperCase();
  for (var i = 0; i < CYLINDERS.length; i++) {
    if (m.indexOf(CYLINDERS[i].model) >= 0) return CYLINDERS[i];
  }
  return null;
}

// Complete nozzle catalog: [drillDiameter(in), orificeArea(in²)]

var NOZZLE_CATALOG = {
  "70704-2":[[0.0781,0.0383],[0.0785,0.0387],[0.081,0.0412],[0.082,0.0422],[0.086,0.0465],[0.089,0.0498],[0.0935,0.0549],[0.0937,0.0552],[0.096,0.0579],[0.098,0.0603],[0.0998,0.0626],[0.1015,0.0647],[0.104,0.068],[0.1065,0.0713],[0.1094,0.0752],[0.11,0.076],[0.111,0.0774],[0.113,0.0802],[0.116,0.0845],[0.12,0.0905],[0.125,0.0982],[0.1285,0.1037],[0.136,0.1162],[0.1406,0.1242],[0.144,0.1303],[0.147,0.1358],[0.1495,0.1404],[0.152,0.1452],[0.154,0.149],[0.1562,0.1533],[0.157,0.1549],[0.159,0.1588],[0.161,0.1629],[0.166,0.1731],[0.1695,0.1805],[0.1719,0.1857],[0.173,0.188],[0.177,0.1968],[0.18,0.2036],[0.182,0.2081],[0.185,0.215],[0.1875,0.2209],[0.189,0.2244],[0.191,0.2292],[0.1935,0.2353],[0.196,0.2414]],
  "70704-3":[[0.0781,0.0383],[0.0785,0.0387],[0.081,0.0412],[0.082,0.0422],[0.086,0.0465],[0.089,0.0498],[0.0935,0.0549],[0.0937,0.0552],[0.096,0.0579],[0.098,0.0603],[0.0998,0.0626],[0.1015,0.0647],[0.104,0.068],[0.1065,0.0713],[0.1094,0.0752],[0.11,0.076],[0.111,0.0774],[0.113,0.0802],[0.116,0.0845],[0.12,0.0905],[0.125,0.0982],[0.1285,0.1037],[0.136,0.1162],[0.1406,0.1242],[0.144,0.1303],[0.147,0.1358],[0.1495,0.1404],[0.152,0.1452],[0.154,0.149],[0.1562,0.1533],[0.157,0.1549],[0.159,0.1588],[0.161,0.1629],[0.166,0.1731],[0.1695,0.1805],[0.1719,0.1857],[0.173,0.188],[0.177,0.1968],[0.18,0.2036],[0.182,0.2081],[0.185,0.215],[0.1875,0.2209],[0.189,0.2244],[0.191,0.2292],[0.1935,0.2353],[0.196,0.2414]],
  "70705-2":[[0.136,0.1162],[0.1406,0.1242],[0.144,0.1303],[0.147,0.1358],[0.1495,0.1404],[0.152,0.1452],[0.154,0.149],[0.1562,0.1533],[0.157,0.1549],[0.159,0.1588],[0.161,0.1629],[0.166,0.1731],[0.1695,0.1805],[0.1719,0.1857],[0.173,0.188],[0.177,0.1968],[0.18,0.2036],[0.182,0.2081],[0.185,0.215],[0.1875,0.2209],[0.189,0.2244],[0.191,0.2292],[0.1935,0.2353],[0.196,0.2414],[0.199,0.2488],[0.201,0.2538],[0.2031,0.2592],[0.204,0.2615],[0.2051,0.2643],[0.209,0.2745],[0.213,0.2851],[0.2187,0.3005],[0.221,0.3069],[0.228,0.3266],[0.234,0.344],[0.2344,0.3452],[0.238,0.3559],[0.242,0.368],[0.246,0.3802],[0.25,0.3927],[0.257,0.415],[0.261,0.428]],
  "70705-3":[[0.136,0.1162],[0.1406,0.1242],[0.144,0.1303],[0.147,0.1358],[0.1495,0.1404],[0.152,0.1452],[0.154,0.149],[0.1562,0.1533],[0.157,0.1549],[0.159,0.1588],[0.161,0.1629],[0.166,0.1731],[0.1695,0.1805],[0.1719,0.1857],[0.173,0.188],[0.177,0.1968],[0.18,0.2036],[0.182,0.2081],[0.185,0.215],[0.1875,0.2209],[0.189,0.2244],[0.191,0.2292],[0.1935,0.2353],[0.196,0.2414],[0.199,0.2488],[0.201,0.2538],[0.2031,0.2592],[0.204,0.2615],[0.2051,0.2643],[0.209,0.2745],[0.213,0.2851],[0.2187,0.3005],[0.221,0.3069],[0.228,0.3266],[0.234,0.344],[0.2344,0.3452],[0.238,0.3559],[0.242,0.368],[0.246,0.3802],[0.25,0.3927],[0.257,0.415],[0.261,0.428]],
  "70706-2":[[0.166,0.1731],[0.1695,0.1805],[0.1719,0.1857],[0.173,0.188],[0.177,0.1968],[0.18,0.2036],[0.182,0.2081],[0.185,0.215],[0.1875,0.2209],[0.189,0.2244],[0.191,0.2292]],
  "70706-3":[[0.166,0.1731],[0.1695,0.1805],[0.1719,0.1857],[0.173,0.188],[0.177,0.1968],[0.18,0.2036],[0.182,0.2081],[0.185,0.215],[0.1875,0.2209],[0.189,0.2244],[0.191,0.2292]],
  "70707-2":[[0.2187,0.3005],[0.221,0.3069],[0.228,0.3266],[0.234,0.344],[0.2344,0.3452],[0.238,0.3559],[0.242,0.368],[0.246,0.3802],[0.25,0.3927],[0.257,0.415],[0.261,0.428],[0.2656,0.4432],[0.266,0.4446],[0.272,0.4649],[0.277,0.4821],[0.281,0.4961],[0.2812,0.4968],[0.29,0.5284],[0.295,0.5468],[0.2969,0.5539],[0.302,0.5731],[0.3125,0.6136],[0.316,0.6274],[0.323,0.6555],[0.3281,0.6764],[0.332,0.6926],[0.339,0.7221],[0.3437,0.7422],[0.348,0.7609],[0.358,0.8053],[0.3594,0.8116],[0.368,0.8509],[0.375,0.8836],[0.377,0.893],[0.386,0.9362],[0.3906,0.9586],[0.397,0.9903],[0.404,1.0255],[0.4062,1.0367],[0.413,1.0717],[0.4219,1.1184],[0.4375,1.2026]],
  "70707-3":[[0.2187,0.3005],[0.221,0.3069],[0.228,0.3266],[0.234,0.344],[0.2344,0.3452],[0.238,0.3559],[0.242,0.368],[0.246,0.3802],[0.25,0.3927],[0.257,0.415],[0.261,0.428],[0.2656,0.4432],[0.266,0.4446],[0.272,0.4649],[0.277,0.4821],[0.281,0.4961],[0.2812,0.4968],[0.29,0.5284],[0.295,0.5468],[0.2969,0.5539],[0.302,0.5731],[0.3125,0.6136],[0.316,0.6274],[0.323,0.6555],[0.3281,0.6764],[0.332,0.6926],[0.339,0.7221],[0.3437,0.7422],[0.348,0.7609],[0.358,0.8053],[0.3594,0.8116],[0.368,0.8509],[0.375,0.8836],[0.377,0.893],[0.386,0.9362],[0.3906,0.9586],[0.397,0.9903],[0.404,1.0255],[0.4062,1.0367],[0.413,1.0717],[0.4219,1.1184],[0.4375,1.2026]],
  "70708-2":[[0.257,0.415],[0.261,0.428],[0.2656,0.4432],[0.266,0.4446],[0.272,0.4649],[0.277,0.4821],[0.281,0.4961],[0.2812,0.4968],[0.29,0.5284],[0.295,0.5468],[0.2969,0.5539],[0.302,0.5731],[0.3125,0.6136],[0.316,0.6274],[0.323,0.6555],[0.3281,0.6764],[0.332,0.6926],[0.339,0.7221],[0.3437,0.7422],[0.348,0.7609],[0.358,0.8053],[0.3594,0.8116],[0.368,0.8509],[0.375,0.8836],[0.377,0.893],[0.386,0.9362],[0.3906,0.9586],[0.397,0.9903],[0.404,1.0255],[0.4062,1.0367],[0.413,1.0717],[0.4219,1.1184],[0.4375,1.2026],[0.4531,1.2899],[0.4688,1.3809],[0.4844,1.4743],[0.5,1.5708]],
  "70708-3":[[0.257,0.415],[0.261,0.428],[0.2656,0.4432],[0.266,0.4446],[0.272,0.4649],[0.277,0.4821],[0.281,0.4961],[0.2812,0.4968],[0.29,0.5284],[0.295,0.5468],[0.2969,0.5539],[0.302,0.5731],[0.3125,0.6136],[0.316,0.6274],[0.323,0.6555],[0.3281,0.6764],[0.332,0.6926],[0.339,0.7221],[0.3437,0.7422],[0.348,0.7609],[0.358,0.8053],[0.3594,0.8116],[0.368,0.8509],[0.375,0.8836],[0.377,0.893],[0.386,0.9362],[0.3906,0.9586],[0.397,0.9903],[0.404,1.0255],[0.4062,1.0367],[0.413,1.0717],[0.4219,1.1184],[0.4375,1.2026],[0.4531,1.2899],[0.4688,1.3809],[0.4844,1.4743],[0.5,1.5708]],
  "70709-2":[[0.3281,0.6764],[0.332,0.6926],[0.339,0.7221],[0.3437,0.7422],[0.348,0.7609],[0.358,0.8053],[0.3594,0.8116],[0.368,0.8509],[0.375,0.8836],[0.377,0.893],[0.386,0.9362],[0.3906,0.9586],[0.397,0.9903],[0.404,1.0255],[0.4062,1.0367],[0.413,1.0717],[0.4219,1.1184],[0.4375,1.2026],[0.4531,1.2899],[0.4688,1.3809]],
  "70709-3":[[0.3281,0.6764],[0.332,0.6926],[0.339,0.7221],[0.3437,0.7422],[0.348,0.7609],[0.358,0.8053],[0.3594,0.8116],[0.368,0.8509],[0.375,0.8836],[0.377,0.893],[0.386,0.9362],[0.3906,0.9586],[0.397,0.9903],[0.404,1.0255],[0.4062,1.0367],[0.413,1.0717],[0.4219,1.1184],[0.4375,1.2026],[0.4531,1.2899],[0.4688,1.3809]]
};


// Nozzle model info
var NOZZLE_MODELS = {
  "70704": { size: '1/2"',  pipeMM: 15 },
  "70705": { size: '3/4"',  pipeMM: 20 },
  "70706": { size: '1"',    pipeMM: 25 },
  "70707": { size: '1-1/4"',pipeMM: 32 },
  "70708": { size: '1-1/2"',pipeMM: 40 },
  "70709": { size: '2"',    pipeMM: 50 },
};

// Find closest nozzle for a given required orifice area
function findNozzle(requiredAreaIn2, nozzleType, pipeDiamMM) {
  // Determine which nozzle model to use based on pipe diameter
  var modelBase = null;
  var entries = Object.entries(NOZZLE_MODELS);
  for (var i = 0; i < entries.length; i++) {
    if (entries[i][1].pipeMM === parseInt(pipeDiamMM)) { modelBase = entries[i][0]; break; }
  }
  // If pipe is larger than nozzle, use largest nozzle that fits
  if (!modelBase) {
    var pd = parseInt(pipeDiamMM) || 25;
    for (var j = entries.length - 1; j >= 0; j--) {
      if (entries[j][1].pipeMM <= pd) { modelBase = entries[j][0]; break; }
    }
  }
  if (!modelBase) modelBase = "70706"; // default 1"

  var typeCode = (nozzleType || "").indexOf("360") >= 0 ? "3" : "2";
  var catalogKey = modelBase + "-" + typeCode;
  var options = NOZZLE_CATALOG[catalogKey];
  if (!options || options.length === 0) return null;

  // Check orifice area ratio constraints
  var openPipe = OPEN_PIPE_AREA_IN2[String(parseInt(pipeDiamMM))] || 0.864;
  var minRatio = parseInt(pipeDiamMM) <= 15 ? 0.10 : 0.20;
  var maxRatio = 0.813;
  var minArea = openPipe * minRatio;
  var maxArea = openPipe * maxRatio;

  // Find closest valid option
  var best = null, bestDiff = Infinity;
  for (var k = 0; k < options.length; k++) {
    var area = options[k][1];
    if (area < minArea || area > maxArea) continue;
    var diff = Math.abs(area - requiredAreaIn2);
    if (diff < bestDiff) { bestDiff = diff; best = options[k]; }
  }

  if (!best) {
    // No valid option within constraints — pick closest anyway
    for (var m = 0; m < options.length; m++) {
      var d2 = Math.abs(options[m][1] - requiredAreaIn2);
      if (d2 < bestDiff) { bestDiff = d2; best = options[m]; }
    }
  }

  if (!best) return null;
  var pnPrefix = "CR " + modelBase + "-" + typeCode + "-";
  var drillStr = best[0].toFixed(4);
  return {
    drillDiameter: best[0],
    orificeArea: best[1],
    orificeAreaMM2: best[1] * 645.16, // convert in² to mm²
    partNumber: pnPrefix + drillStr,
    nozzleSize: NOZZLE_MODELS[modelBase].size,
    modelBase: modelBase,
    type: typeCode === "3" ? "360° Central" : "180° Sidewall",
  };
}


// ─── SECTION 2: HYDRAULIC CALCULATIONS ───────────────────────

// Swamee-Jain approximation for Darcy friction factor
function frictionFactor(Re, D) {
  if (Re < 2300) return 64 / Math.max(Re, 1);
  var eps = AGENT.pipeRoughness;
  var term = eps / (3.7 * D) + 5.74 / Math.pow(Re, 0.9);
  return 0.25 / Math.pow(Math.log10(term), 2);
}

// Pressure drop through a pipe segment (Pa)
function pressureDrop(massFlowRate, diamMM, eqLengthM) {
  var D = PIPE_ID[String(diamMM)] || PIPE_ID["25"];
  var A = Math.PI * D * D / 4;
  var rho = AGENT.liquidDensity;
  var v = massFlowRate / (rho * A);
  var Re = rho * Math.abs(v) * D / AGENT.viscosity;
  var f = frictionFactor(Re, D);
  // ΔP = f × (L/D) × (ρv²/2)
  var dp = f * (eqLengthM / D) * (rho * v * v / 2);
  return dp; // Pa
}

// Convert equivalent length from feet to meters
function ftToM(ft) { return ft * 0.3048; }

// Calculate equivalent length for a pipe segment including fittings
function segmentEquivLength(edge, network) {
  var pipeLen = parseFloat(edge.length) || 0; // in user units (meters)
  var diamMM = edge.diameter || "25";

  if (edge.edgeType === "hose") {
    // Flex hose — use Table 3-9
    var hoseData = EQ_LEN_VALVE_HOSE[String(diamMM)];
    return hoseData ? ftToM(hoseData[1]) : ftToM(7.0);
  }

  // Start with pipe physical length
  var totalEqLen = pipeLen;

  // Add fitting equivalent lengths based on node types at endpoints
  var fromNode = nById(network, edge.from);
  var toNode = nById(network, edge.to);
  var fittings = EQ_LEN_THREADED[String(diamMM)] || EQ_LEN_THREADED["25"];
  // [90°elbow, 45°elbow, thruTee, sideTee, unionCoupling, unionElbow]

  // Check if endpoint is an elbow (junction with 2 connections, direction change)
  if (toNode && toNode.type === "junction") {
    var toEdges = eFor(network, toNode.id);
    if (toEdges.length === 2) {
      var d1 = toEdges[0].direction, d2 = toEdges[1].direction;
      if (d1 && d2 && d1 !== d2) {
        totalEqLen += ftToM(fittings[0]); // 90° elbow
      }
    }
  }

  // Check if endpoint is a tee
  if (toNode && toNode.type === "tee") {
    var teeEdges = eFor(network, toNode.id);
    // Determine if this is thru or side outlet
    // Simple heuristic: if pipe continues in same direction = thru, otherwise = side
    var inDir = edge.direction;
    var outEdges = teeEdges.filter(function(e) { return e.from === toNode.id; });
    var isSide = outEdges.some(function(e) { return e.direction !== inDir; });
    totalEqLen += ftToM(isSide ? fittings[3] : fittings[2]); // sideTee or thruTee
  }

  return totalEqLen;
}

// Helper: find node by ID in network
function nById(net, id) { return net.nodes.find(function(n) { return n.id === id; }); }
function eFor(net, id) { return net.edges.filter(function(e) { return e.from === id || e.to === id; }); }

// Position-based straight-through detection for tee fittings (item 11)
function isStraightThrough(inEdge, outEdge, network) {
  var teeNode = nById(network, inEdge.to);
  var fromNode = nById(network, inEdge.from);
  var toNode = nById(network, outEdge.to);
  if (teeNode && fromNode && toNode &&
      fromNode.x !== undefined && teeNode.x !== undefined && toNode.x !== undefined) {
    var inAngle = Math.atan2(teeNode.y - fromNode.y, teeNode.x - fromNode.x);
    var outAngle = Math.atan2(toNode.y - teeNode.y, toNode.x - teeNode.x);
    var diff = Math.abs(outAngle - inAngle);
    if (diff > Math.PI) diff = 2 * Math.PI - diff;
    return diff < Math.PI / 4; // < 45° = straight through
  }
  // Fallback to direction property
  return inEdge.direction && outEdge.direction && inEdge.direction === outEdge.direction;
}

// Path-aware segment equivalent length — uses next edge in path for tee detection
function segEquivForPath(edge, pathEdges, edgeIdx, network) {
  var pipeLen = parseFloat(edge.length) || 0;
  var diamMM = edge.diameter || "25";

  if (edge.edgeType === "hose") {
    var hoseData = EQ_LEN_VALVE_HOSE[String(diamMM)];
    return hoseData ? ftToM(hoseData[1]) : ftToM(7.0);
  }

  var totalEqLen = pipeLen;
  var fittings = EQ_LEN_THREADED[String(diamMM)] || EQ_LEN_THREADED["25"];
  var toNode = nById(network, edge.to);

  // Elbow at junction with direction change
  if (toNode && toNode.type === "junction") {
    var toEdges = eFor(network, toNode.id);
    if (toEdges.length === 2) {
      var fromNode = nById(network, edge.from);
      if (fromNode && fromNode.x !== undefined && toNode.x !== undefined) {
        var otherEdge = toEdges.find(function(e) {
          return (e.from === toNode.id && e.to !== edge.from) || (e.to === toNode.id && e.from !== edge.from);
        });
        var otherNode = otherEdge ? nById(network, otherEdge.from === toNode.id ? otherEdge.to : otherEdge.from) : null;
        if (otherNode && otherNode.x !== undefined) {
          var inA = Math.atan2(toNode.y - fromNode.y, toNode.x - fromNode.x);
          var outA = Math.atan2(otherNode.y - toNode.y, otherNode.x - toNode.x);
          var aDiff = Math.abs(outA - inA);
          if (aDiff > Math.PI) aDiff = 2 * Math.PI - aDiff;
          if (aDiff > Math.PI / 6) totalEqLen += ftToM(aDiff > Math.PI / 3 ? fittings[0] : fittings[1]);
        }
      } else {
        var d1 = toEdges[0].direction, d2 = toEdges[1].direction;
        if (d1 && d2 && d1 !== d2) totalEqLen += ftToM(fittings[0]);
      }
    }
  }

  // Tee — use next edge in path to determine thru vs side
  if (toNode && toNode.type === "tee") {
    var nextEdge = (edgeIdx + 1 < pathEdges.length) ? pathEdges[edgeIdx + 1] : null;
    var isSide = true;
    if (nextEdge) {
      isSide = !isStraightThrough(edge, nextEdge, network);
    }
    totalEqLen += ftToM(isSide ? fittings[3] : fittings[2]);
  }

  return totalEqLen;
}

// Build edge flow map: for each edge, sum flows of all nozzle paths using that edge
function buildEdgeFlowMap(pathData, nozzleFlows) {
  var map = {};
  for (var i = 0; i < pathData.length; i++) {
    var flow = nozzleFlows[i];
    for (var j = 0; j < pathData[i].path.edges.length; j++) {
      var e = pathData[i].path.edges[j];
      var key = e.from + "|" + e.to;
      map[key] = (map[key] || 0) + flow;
    }
  }
  return map;
}


// ─── SECTION 3: NETWORK SOLVER ──────────────────────────────

// Find all paths from cylinder to nozzles
function findPaths(network) {
  var cylNode = network.nodes.find(function(n) { return n.type === "cylinder"; });
  if (!cylNode) return [];

  var paths = [];
  function dfs(nodeId, path, edgePath) {
    var node = nById(network, nodeId);
    if (!node) return;
    if (node.type === "nozzle") {
      paths.push({ nodes: path.slice(), edges: edgePath.slice(), nozzleId: nodeId, nozzleLabel: node.label });
      return;
    }
    var outEdges = network.edges.filter(function(e) {
      return e.from === nodeId && path.indexOf(e.to) < 0;
    });
    for (var i = 0; i < outEdges.length; i++) {
      path.push(outEdges[i].to);
      edgePath.push(outEdges[i]);
      dfs(outEdges[i].to, path, edgePath);
      path.pop();
      edgePath.pop();
    }
  }

  // Start from hose_end (node after cylinder)
  var hoseEdge = network.edges.find(function(e) { return e.from === cylNode.id; });
  if (!hoseEdge) return [];

  dfs(hoseEdge.to, [cylNode.id, hoseEdge.to], [hoseEdge]);
  return paths;
}

// Calculate total equivalent length for a path
function pathEquivLength(path, network) {
  var total = 0;
  for (var i = 0; i < path.edges.length; i++) {
    total += segmentEquivLength(path.edges[i], network);
  }
  return total;
}

// Solve network pressures and flow rates — iterative solver (v2)
function solveNetwork(network, agentKg, cylModel, tempC, nozzleType) {
  var cyl = findCylinder(cylModel);
  if (!cyl) return { error: "Unknown cylinder model: " + cylModel };

  // Use actual cylinder fill capacity (what physically discharges)
  // instead of minimum calculated agent mass
  var actualAgentKg = Math.max(agentKg, cyl.maxKg ? Math.min(agentKg * 1.15, cyl.maxKg) : agentKg);

  // Starting conditions — apply mean effective pressure factor
  // P0_initial is the peak superpressure; P0_effective is the time-averaged
  // driving pressure during the full discharge cycle
  var P0_initial = cylinderPressure(tempC);
  var P0_bar = P0_initial * MEAN_EFFECTIVE_PRESSURE_FACTOR;
  var P0_pa = P0_bar * 1e5; // Convert bar to Pa

  // Valve+siphon equivalent length
  var valveData = EQ_LEN_VALVE_HOSE[String(cyl.valveMM)];
  var valveSiphonEqLen = valveData ? ftToM(valveData[0]) : ftToM(16.5);

  // Find all paths from cylinder to nozzles
  var paths = findPaths(network);
  if (paths.length === 0) return { error: "No paths found from cylinder to nozzles" };

  // Calculate path equivalent lengths (for initial flow distribution)
  var pathData = paths.map(function(p) {
    var eqLen = valveSiphonEqLen + pathEquivLength(p, network);
    return { path: p, eqLength: eqLen, nozzleId: p.nozzleId, nozzleLabel: p.nozzleLabel };
  });

  // Harmonic mean of path lengths for initial estimate
  var totalInvEqLen = 0;
  for (var i = 0; i < pathData.length; i++) {
    totalInvEqLen += 1 / pathData[i].eqLength;
  }

  // ── Initial flow estimate (8s target) ──
  var targetTime = 8.0;
  var totalFlowRate = actualAgentKg / targetTime;
  var nozzleFlows = pathData.map(function(pd) {
    return totalFlowRate * (1 / pd.eqLength) / totalInvEqLen;
  });

  // ── Segment-by-segment pressure calculation ──
  function calcNozzlePressures(nFlows) {
    var tFlow = 0;
    for (var s = 0; s < nFlows.length; s++) tFlow += nFlows[s];
    var efm = buildEdgeFlowMap(pathData, nFlows);
    var pressures = [];
    for (var pi = 0; pi < pathData.length; pi++) {
      var p_pa = P0_pa;
      // Valve/siphon sees total flow
      p_pa -= pressureDrop(tFlow, String(cyl.valveMM), valveSiphonEqLen);
      // Walk each edge with its actual local flow
      var edges = pathData[pi].path.edges;
      for (var ei = 0; ei < edges.length; ei++) {
        var seg = edges[ei];
        var segKey = seg.from + "|" + seg.to;
        var segFlow = efm[segKey] || nFlows[pi];
        var segEq = segEquivForPath(seg, edges, ei, network);
        p_pa -= pressureDrop(segFlow, seg.diameter || "25", segEq);
      }
      pressures.push(Math.max(p_pa, 0));
    }
    return pressures;
  }

  // ── First pass: get initial pressures and select nozzles ──
  var nozzlePressures = calcNozzlePressures(nozzleFlows);

  // Select nozzles from catalog (done ONCE based on initial estimate)
  var selectedNozzles = [];
  for (var n = 0; n < pathData.length; n++) {
    var pipeDiam = "25";
    for (var pe = pathData[n].path.edges.length - 1; pe >= 0; pe--) {
      if (pathData[n].path.edges[pe].edgeType !== "hose") {
        pipeDiam = pathData[n].path.edges[pe].diameter || "25";
        break;
      }
    }
    var nP = nozzlePressures[n];
    var nFlow = nozzleFlows[n];
    var reqArea_m2 = nP > 0 ? nFlow / (AGENT.nozzleCd * Math.sqrt(2 * AGENT.liquidDensity * nP)) : 0;
    var reqArea_in2 = reqArea_m2 / 0.00064516;
    selectedNozzles.push(findNozzle(reqArea_in2, nozzleType, pipeDiam));
  }

  // ── Iterative solver: converge on actual flows and pressures ──
  var MAX_ITER = 20;
  var CONVERGE = 0.001; // 0.1%
  var prevTotal = totalFlowRate;

  for (var iter = 0; iter < MAX_ITER; iter++) {
    // Recalculate pressures with current flows
    nozzlePressures = calcNozzlePressures(nozzleFlows);

    // Calculate actual flow through each selected nozzle: Q = Cd × A × sqrt(2ρP)
    var newFlows = [];
    var newTotal = 0;
    for (var fi = 0; fi < pathData.length; fi++) {
      var nz = selectedNozzles[fi];
      var pNozzle = nozzlePressures[fi];
      if (nz && pNozzle > 0) {
        var A_m2 = nz.orificeArea * 0.00064516; // in² to m²
        var qActual = AGENT.nozzleCd * A_m2 * Math.sqrt(2 * AGENT.liquidDensity * pNozzle);
        newFlows.push(qActual);
        newTotal += qActual;
      } else {
        newFlows.push(nozzleFlows[fi]);
        newTotal += nozzleFlows[fi];
      }
    }

    nozzleFlows = newFlows;
    totalFlowRate = newTotal;

    // Check convergence
    if (iter > 0 && Math.abs(newTotal - prevTotal) / Math.max(prevTotal, 0.001) < CONVERGE) break;
    prevTotal = newTotal;
  }

  // ── Final pressure calculation with converged flows ──
  nozzlePressures = calcNozzlePressures(nozzleFlows);

  // ── Actual discharge time ──
  var dischargeTime = totalFlowRate > 0 ? actualAgentKg / totalFlowRate : 99;

  // ── Build nozzle results with arrival times ──
  var nozzleResults = [];
  for (var ri = 0; ri < pathData.length; ri++) {
    var pd = pathData[ri];
    var pBar = nozzlePressures[ri] / 1e5;

    // Volume-based arrival time (item 8)
    var pathVol = 0;
    pd.path.edges.forEach(function(e) {
      var len = parseFloat(e.length) || 0;
      var d = PIPE_ID[String(e.diameter)] || PIPE_ID["25"];
      pathVol += Math.PI * d * d / 4 * len;
    });
    var volFlow = AGENT.liquidDensity > 0 ? nozzleFlows[ri] / AGENT.liquidDensity : 0;
    var arrivalTime = volFlow > 0 ? pathVol / volFlow : 0;

    nozzleResults.push({
      nozzleId: pd.nozzleId,
      nozzleLabel: pd.nozzleLabel,
      flowRate: nozzleFlows[ri],
      flowRateLbs: nozzleFlows[ri] * 2.20462,
      pressure: pBar,
      pressurePsi: pBar * 14.5038,
      requiredOrificeArea: selectedNozzles[ri] ? selectedNozzles[ri].orificeArea : 0,
      selectedNozzle: selectedNozzles[ri],
      agentDelivered: nozzleFlows[ri] * dischargeTime,
      eqLength: pd.eqLength,
      path: pd.path,
      arrivalTime: arrivalTime,
    });
  }

  // ── Agent in pipe ──
  var pipeVolume = 0;
  network.edges.forEach(function(e) {
    if (e.edgeType === "hose") return;
    var len = parseFloat(e.length) || 0;
    var d = PIPE_ID[String(e.diameter)] || PIPE_ID["25"];
    pipeVolume += Math.PI * d * d / 4 * len;
  });
  var agentInPipe = pipeVolume * AGENT.liquidDensity;
  var pctInPipe = actualAgentKg > 0 ? (agentInPipe / actualAgentKg) * 100 : 0;

  // ── Agent before first tee ──
  var firstTeeNode = network.nodes.find(function(n) { return n.type === "tee"; });
  var agentBeforeTee = 0;
  if (firstTeeNode) {
    var hoseEdge = network.edges.find(function(e) { return e.edgeType === "hose"; });
    if (hoseEdge) {
      var hoseLen = parseFloat(hoseEdge.length) || 0;
      var hoseD = PIPE_ID[String(hoseEdge.diameter)] || PIPE_ID["25"];
      agentBeforeTee += Math.PI * hoseD * hoseD / 4 * hoseLen * AGENT.liquidDensity;
    }
    var walkId = "hose_end";
    var walkVisited = { "cyl": true };
    while (walkId) {
      var walkNode = nById(network, walkId);
      if (!walkNode || walkNode.type === "tee") break;
      walkVisited[walkId] = true;
      var nextEdge = network.edges.find(function(e) {
        return e.from === walkId && !walkVisited[e.to] && e.edgeType !== "hose";
      });
      if (!nextEdge) break;
      var segLen = parseFloat(nextEdge.length) || 0;
      var segD = PIPE_ID[String(nextEdge.diameter)] || PIPE_ID["25"];
      agentBeforeTee += Math.PI * segD * segD / 4 * segLen * AGENT.liquidDensity;
      walkId = nextEdge.to;
    }
  }
  var pctBeforeTee = !firstTeeNode ? "N/A" : (actualAgentKg > 0 ? Math.round((agentBeforeTee / actualAgentKg) * 1000) / 10 : 0);

  // ── Node pressure map (consistent with converged solution) ──
  var nodePressures = {};
  nodePressures["cyl"] = P0_bar;
  var convergedEFM = buildEdgeFlowMap(pathData, nozzleFlows);
  for (var np = 0; np < nozzleResults.length; np++) {
    var nr = nozzleResults[np];
    nodePressures[nr.nozzleId] = nr.pressure;
    var cumP = P0_pa;
    cumP -= pressureDrop(totalFlowRate, String(cyl.valveMM), valveSiphonEqLen);
    for (var k = 0; k < nr.path.edges.length; k++) {
      var npEdge = nr.path.edges[k];
      var npKey = npEdge.from + "|" + npEdge.to;
      var npFlow = convergedEFM[npKey] || nozzleFlows[np];
      var npEqLen = segEquivForPath(npEdge, nr.path.edges, k, network);
      cumP -= pressureDrop(npFlow, npEdge.diameter || "25", npEqLen);
      var endNode = npEdge.to;
      if (nodePressures[endNode] === undefined) {
        nodePressures[endNode] = cumP / 1e5;
      } else {
        // Average with existing value for consistency at shared nodes (item 13)
        nodePressures[endNode] = (nodePressures[endNode] + cumP / 1e5) / 2;
      }
    }
  }

  return {
    startPressure: P0_bar,
    initialPressure: P0_initial,
    dischargeTime: dischargeTime,
    totalFlowRate: totalFlowRate,
    totalFlowRateLbs: totalFlowRate * 2.20462,
    agentKg: actualAgentKg,
    minAgentKg: agentKg,
    pctAgentInPipe: pctInPipe,
    pctAgentBeforeFirstTee: pctBeforeTee,
    nozzleResults: nozzleResults,
    nodePressures: nodePressures,
    pipeVolume: pipeVolume,
    agentInPipe: agentInPipe,
  };
}


// ─── SECTION 4: CONSTRAINT CHECKING ─────────────────────────

function checkConstraints(solveResult) {
  var issues = [];
  var warnings = [];
  var sr = solveResult;

  // 1. Discharge time — NFPA 2001 max is 10s; no formal minimum
  if (sr.dischargeTime < 5) {
    warnings.push({ code: "DISCHARGE_TIME_LOW", message: "Discharge time " + sr.dischargeTime.toFixed(1) + "s is unusually fast — verify pipe sizing and nozzle selection", value: sr.dischargeTime });
  }
  if (sr.dischargeTime > CONSTRAINTS.maxDischargeTime) {
    issues.push({ code: "DISCHARGE_TIME_HIGH", message: "Discharge time " + sr.dischargeTime.toFixed(1) + "s exceeds NFPA 2001 maximum of " + CONSTRAINTS.maxDischargeTime + "s", value: sr.dischargeTime, limit: CONSTRAINTS.maxDischargeTime });
  }

  // 2. Max agent in pipe
  if (sr.pctAgentInPipe > CONSTRAINTS.maxAgentInPipe) {
    issues.push({ code: "AGENT_IN_PIPE", message: "Agent in pipe " + sr.pctAgentInPipe.toFixed(1) + "% exceeds maximum " + CONSTRAINTS.maxAgentInPipe + "%", value: sr.pctAgentInPipe, limit: CONSTRAINTS.maxAgentInPipe });
  }

  // 3. Min agent before first tee (skip if N/A — no tee in system)
  if (sr.pctAgentBeforeFirstTee !== "N/A" && sr.pctAgentBeforeFirstTee < CONSTRAINTS.minAgentBeforeFirstTee && sr.nozzleResults.length > 1) {
    issues.push({ code: "AGENT_BEFORE_TEE", message: "Agent before first tee " + sr.pctAgentBeforeFirstTee.toFixed(1) + "% is below minimum " + CONSTRAINTS.minAgentBeforeFirstTee + "%", value: sr.pctAgentBeforeFirstTee, limit: CONSTRAINTS.minAgentBeforeFirstTee });
  }

  // 4. Minimum nozzle pressure
  sr.nozzleResults.forEach(function(nr) {
    if (nr.pressure < CONSTRAINTS.minNozzlePressure) {
      issues.push({ code: "NOZZLE_PRESSURE", message: "Nozzle " + nr.nozzleLabel + " pressure " + nr.pressure.toFixed(2) + " bar is below minimum " + CONSTRAINTS.minNozzlePressure.toFixed(2) + " bar (68 psig)", value: nr.pressure, limit: CONSTRAINTS.minNozzlePressure, nozzle: nr.nozzleLabel });
    }
  });

  // 5. Flow rate limits (Table 3-4 — design guidelines, not NFPA pass/fail)
  sr.nozzleResults.forEach(function(nr) {
    if (nr.path && nr.path.edges) {
      nr.path.edges.forEach(function(edge) {
        if (edge.edgeType === "hose") return;
        var diam = edge.diameter || "25";
        var limits = FLOW_LIMITS[String(diam)];
        if (limits) {
          var flowLbs = nr.flowRateLbs;
          if (flowLbs < limits[0]) {
            warnings.push({ code: "FLOW_LOW", message: "Flow in \u00F8" + diam + "mm pipe (" + flowLbs.toFixed(2) + " lb/s) is below recommended minimum " + limits[0] + " lb/s", value: flowLbs, limit: limits[0] });
          }
          if (flowLbs > limits[1]) {
            warnings.push({ code: "FLOW_HIGH", message: "Flow in \u00F8" + diam + "mm pipe (" + flowLbs.toFixed(2) + " lb/s) exceeds recommended maximum " + limits[1] + " lb/s — consider using larger pipe diameter", value: flowLbs, limit: limits[1] });
          }
        }
      });
    }
  });

  // 6. Arrival time imbalance (volume-based calculation)
  if (sr.nozzleResults.length >= 2) {
    var times = sr.nozzleResults.map(function(nr) { return nr.arrivalTime || nr.eqLength; });
    var maxT = Math.max.apply(null, times), minT = Math.max(Math.min.apply(null, times), 0.001);
    var imbalance = maxT - minT;
    if (imbalance > CONSTRAINTS.maxArrivalImbalance) {
      warnings.push({ code: "IMBALANCE", message: "Arrival time imbalance " + imbalance.toFixed(2) + "s between nozzles (max: " + maxT.toFixed(2) + "s, min: " + minT.toFixed(2) + "s). Limit: " + CONSTRAINTS.maxArrivalImbalance + "s", value: imbalance, limit: CONSTRAINTS.maxArrivalImbalance });
    }
  }

  var passed = issues.length === 0;
  return { passed: passed, issues: issues, warnings: warnings };
}


// ─── SECTION 5: AUTO-FIX OPTIMIZER ──────────────────────────

function optimizeSystem(network, agentKg, cylModel, tempC, nozzleType) {
  var fixes = [];
  var bestResult = null;

  // Strategy 1: Try increasing pipe diameters at bottleneck segments
  var pipeSizes = ["15", "20", "25", "32", "40", "50", "65", "80", "100"];
  var modified = JSON.parse(JSON.stringify(network));

  // Find the segment with highest pressure drop contribution
  var baseResult = solveNetwork(network, agentKg, cylModel, tempC, nozzleType);
  if (!baseResult.error) {
    var check = checkConstraints(baseResult);
    if (check.passed) return { success: true, fixes: [], result: baseResult, check: check };

    // Try increasing each pipe segment's diameter one step
    for (var attempt = 0; attempt < 3; attempt++) {
      var improved = false;
      for (var i = 0; i < modified.edges.length; i++) {
        var edge = modified.edges[i];
        if (edge.edgeType === "hose") continue;

        var currentIdx = pipeSizes.indexOf(String(edge.diameter));
        if (currentIdx < 0 || currentIdx >= pipeSizes.length - 1) continue;

        var origDiam = edge.diameter;
        edge.diameter = pipeSizes[currentIdx + 1];
        var tryResult = solveNetwork(modified, agentKg, cylModel, tempC, nozzleType);
        if (!tryResult.error) {
          var tryCheck = checkConstraints(tryResult);
          if (tryCheck.passed) {
            fixes.push({ type: "pipe_diameter", from: (nById(modified, edge.from) || {}).label, to: (nById(modified, edge.to) || {}).label, oldValue: origDiam + "mm", newValue: edge.diameter + "mm" });
            return { success: true, fixes: fixes, result: tryResult, check: tryCheck, network: modified };
          }
          // Check if it improved (fewer issues)
          var baseIssues = checkConstraints(solveNetwork(network, agentKg, cylModel, tempC, nozzleType)).issues.length;
          if (tryCheck.issues.length < baseIssues) {
            fixes.push({ type: "pipe_diameter", from: (nById(modified, edge.from) || {}).label, to: (nById(modified, edge.to) || {}).label, oldValue: origDiam + "mm", newValue: edge.diameter + "mm" });
            improved = true;
          } else {
            edge.diameter = origDiam; // revert
          }
        } else {
          edge.diameter = origDiam; // revert
        }
      }
      if (!improved) break;
    }

    // Strategy 2: Try next larger cylinder
    var cylData = findCylinder(cylModel);
    if (cylData) {
      for (var c = 0; c < CYLINDERS.length; c++) {
        if (CYLINDERS[c].maxKg > cylData.maxKg) {
          var largerCyl = CYLINDERS[c];
          var newAgent = Math.min(agentKg, largerCyl.maxKg);
          var cylResult = solveNetwork(modified, newAgent, "CR " + largerCyl.model + "-E", tempC, nozzleType);
          if (!cylResult.error) {
            var cylCheck = checkConstraints(cylResult);
            if (cylCheck.passed) {
              fixes.push({ type: "cylinder_upsize", oldValue: cylModel, newValue: "CR " + largerCyl.model + "-E (" + largerCyl.lbs + " lb)" });
              return { success: true, fixes: fixes, result: cylResult, check: cylCheck, network: modified };
            }
          }
        }
      }
    }
  }

  // Could not auto-fix
  return { success: false, fixes: fixes, result: baseResult, check: baseResult ? checkConstraints(baseResult) : null };
}


// ─── SECTION 6: PUBLIC API ──────────────────────────────────

// Main entry point
export function runFlowCheck(pipeNetwork, calcResult, formData) {
  if (!pipeNetwork || !pipeNetwork.network) {
    return { error: "No pipe network data provided" };
  }

  var network = pipeNetwork.network;
  var encResult = calcResult || {};
  var agentKg = (encResult.agentLbs || 0) * 0.453592; // Convert lbs to kg
  if (encResult.agentKg) agentKg = encResult.agentKg;
  var cylModel = encResult.model || encResult.cylinderModel || "";
  var isMetric = (formData.unitSystem || "").indexOf("Metric") >= 0;
  var rawTemp = parseFloat(formData.minTemp);
  var tempC = isNaN(rawTemp) ? 20 : (isMetric ? rawTemp : (rawTemp - 32) * 5 / 9);
  var nozzleType = formData.nozzleType || "180° Sidewall";

  // Run solver
  var result = solveNetwork(network, agentKg, cylModel, tempC, nozzleType);
  if (result.error) return { error: result.error, passed: false };

  // Check constraints
  var check = checkConstraints(result);

  return {
    passed: check.passed,
    issues: check.issues,
    warnings: check.warnings,
    dischargeTime: result.dischargeTime,
    startPressure: result.startPressure,
    totalFlowRate: result.totalFlowRate,
    pctAgentInPipe: result.pctAgentInPipe,
    pctAgentBeforeFirstTee: result.pctAgentBeforeFirstTee,
    nozzleResults: result.nozzleResults,
    nodePressures: result.nodePressures,
    agentKg: agentKg,
    agentInPipe: result.agentInPipe,
  };
}

// Auto-fix entry point
export function runOptimizer(pipeNetwork, calcResult, formData) {
  if (!pipeNetwork || !pipeNetwork.network) {
    return { error: "No pipe network data provided", success: false };
  }

  var network = pipeNetwork.network;
  var encResult = calcResult || {};
  var agentKg = (encResult.agentLbs || 0) * 0.453592;
  if (encResult.agentKg) agentKg = encResult.agentKg;
  var cylModel = encResult.model || encResult.cylinderModel || "";
  var isMetric = (formData.unitSystem || "").indexOf("Metric") >= 0;
  var rawTemp = parseFloat(formData.minTemp);
  var tempC = isNaN(rawTemp) ? 20 : (isMetric ? rawTemp : (rawTemp - 32) * 5 / 9);
  var nozzleType = formData.nozzleType || "180° Sidewall";

  return optimizeSystem(network, agentKg, cylModel, tempC, nozzleType);
}

// Export constraint values for UI use
export var FLOW_CONSTRAINTS = CONSTRAINTS;
export var CYLINDER_DATA = CYLINDERS;
export { findNozzle, findCylinder };
